import { Monitor, Moon, Sun } from "lucide-react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { setAdminLocale } from "../../i18n-provider";
import { IconButton, Menu, MenuContent, MenuLabel, MenuRadioGroup, MenuRadioItem, MenuTrigger, Panel } from "../../ui";
import { setThemePreference, useTheme, type ThemePreference } from "../../ui/shell";
import styles from "./auth.module.css";

const ICON_PROPS = { size: 16, strokeWidth: 1.75, "aria-hidden": true } as const;

export function AuthLayout({ title, subtitle, children }: { title: string; subtitle: string; children: ReactNode }) {
  const { t, i18n } = useTranslation();
  const { preference } = useTheme();
  const themes: { value: ThemePreference; label: string; icon: ReactNode }[] = [
    { value: "light", label: t("nav.shell.themeLight"), icon: <Sun {...ICON_PROPS} /> },
    { value: "dark", label: t("nav.shell.themeDark"), icon: <Moon {...ICON_PROPS} /> },
    { value: "system", label: t("nav.shell.themeSystem"), icon: <Monitor {...ICON_PROPS} /> },
  ];
  const locales = [
    { value: "pt-BR", label: t("nav.shell.languagePortuguese") },
    { value: "en-US", label: t("nav.shell.languageEnglish") },
  ];
  const activeTheme = themes.find((theme) => theme.value === preference) ?? themes[2]!;

  return (
    <div className={styles.page}>
      <div className={styles.preferences}>
        <Menu>
          <MenuTrigger asChild>
            <IconButton variant="subtle" size="sm" showTooltip={false} label={t("nav.shell.theme")} icon={activeTheme.icon} />
          </MenuTrigger>
          <MenuContent align="end">
            <MenuLabel>{t("nav.shell.theme")}</MenuLabel>
            <MenuRadioGroup value={preference} onValueChange={(value) => setThemePreference(value as ThemePreference)}>
              {themes.map((theme) => (
                <MenuRadioItem key={theme.value} value={theme.value}>
                  {theme.label}
                </MenuRadioItem>
              ))}
            </MenuRadioGroup>
          </MenuContent>
        </Menu>
        <Menu>
          <MenuTrigger asChild>
            <IconButton
              variant="subtle"
              size="sm"
              showTooltip={false}
              label={t("nav.shell.language")}
              icon={<span className={styles.localeCode}>{i18n.language.slice(0, 2).toUpperCase()}</span>}
            />
          </MenuTrigger>
          <MenuContent align="end">
            <MenuLabel>{t("nav.shell.language")}</MenuLabel>
            <MenuRadioGroup value={i18n.language} onValueChange={setAdminLocale}>
              {locales.map((locale) => (
                <MenuRadioItem key={locale.value} value={locale.value} lang={locale.value}>
                  {locale.label}
                </MenuRadioItem>
              ))}
            </MenuRadioGroup>
          </MenuContent>
        </Menu>
      </div>
      <main className={styles.main}>
        <Panel padding="none" className={styles.card}>
          <div className={styles.cardBody}>
            <div className={styles.intro}>
              <p className={styles.brand}>
                <img src="/mark.svg" alt="" className={styles.brandMark} />
                <span className={styles.brandName}>{t("common.brandName")}</span>
              </p>
              <h1 className={styles.title}>{title}</h1>
              <p className={styles.subtitle}>{subtitle}</p>
            </div>
            {children}
          </div>
        </Panel>
      </main>
    </div>
  );
}
